/* =====================================================================
   pf-delivery.js —— 配送估算 + 尺码导购（商品详情页 / 目录页详情 共用）
   2026-10-08 新增。唯一实现，禁止复制第二份。

   提供两个能力：
     PFDelivery.delivery(opts)   预计送达 + 运费起步（异步填充，失败静默不留空壳）
     PFDelivery.sizeGuide(opts)  尺码导购弹窗（双 tab + Inches/Cm）

   数据源全部走 bridge.highdeep.net（Printful key 不进前端）：
     POST /shipping/rates   { catalogVariantId, quantity, country }  -> rates[]
     GET  /pf/sizes         ?product_id=<pid>                        -> size_tables[]

   访客国家取 window.Shopify.country（= localization.country.iso_code，与结账同源）。
   绝不使用 IP 地理定位：免邮门槛/送达日期错了=当着客户毁约（三源实测会分歧）。
   ===================================================================== */
(function () {
  if (window.PFDelivery) return;

  var BRIDGE_DEFAULT = 'https://bridge.highdeep.net';
  var NOANIM = /(?:^|[?&])pfm=off(?:&|$)/.test(String(location.search || '')) ||
    !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var TTL_MS = 10 * 60 * 1000;

  function bridgeOf(b) { return String(b || BRIDGE_DEFAULT).replace(/\/+$/, ''); }

  function visitorCountry() {
    try { if (window.Shopify && window.Shopify.country) return String(window.Shopify.country).toUpperCase(); } catch (e) {}
    return '';
  }

  function flagEmoji(cc) {
    cc = String(cc || '').toUpperCase();
    if (!/^[A-Z]{2}$/.test(cc)) return '';
    try { return cc.replace(/./g, function (c) { return String.fromCodePoint(127397 + c.charCodeAt(0)); }); } catch (e) { return ''; }
  }

  function countryName(cc) {
    cc = String(cc || '').toUpperCase();
    try {
      if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
        var n = new Intl.DisplayNames(['en'], { type: 'region' }).of(cc);
        if (n && n !== cc) return n;
      }
    } catch (e) {}
    return cc;
  }

  /* 日期区间：同月 "Oct 15-17"；跨月 "Oct 30 - Nov 2" */
  function fmtRange(a, b) {
    var d1 = new Date(String(a) + 'T00:00:00');
    var d2 = new Date(String(b || a) + 'T00:00:00');
    if (isNaN(d1.getTime())) return '';
    if (isNaN(d2.getTime())) d2 = d1;
    var m1 = d1.toLocaleString('en-US', { month: 'short' });
    var m2 = d2.toLocaleString('en-US', { month: 'short' });
    if (m1 === m2) return m1 + ' ' + d1.getDate() + '\u2013' + d2.getDate();
    return m1 + ' ' + d1.getDate() + ' \u2013 ' + m2 + ' ' + d2.getDate();
  }

  function money(v) {
    var n = parseFloat(v);
    if (!isFinite(n)) return '';
    return '$' + n.toFixed(2);
  }

  /* ---------- 会话级缓存（bridge 侧也有 10min 缓存，这里省一次往返） ---------- */
  function cacheGet(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || !o.t || Date.now() - o.t > TTL_MS) return null;
      return o.v;
    } catch (e) { return null; }
  }
  function cacheSet(key, v) {
    try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), v: v })); } catch (e) {}
  }

  /* ---------- CSS 注入（一次性；全部走 pf token） ---------- */
  function injectCSS() {
    if (document.getElementById('pfdlv-css')) return;
    var s = document.createElement('style');
    s.id = 'pfdlv-css';
    s.textContent = [
      /* 2026-10-20（用户定·丙）：**不再淡入** —— 原来 opacity:0 + .on 淡入 0.28s，每次都要闪一下；
   骨架与内容都是同一元素，直接替换即可。 */
      '.pfdlv{margin-top:var(--pf-sp-3,12px);margin-bottom:var(--pf-sp-5,24px);font-size:.85rem;line-height:1.5;color:var(--color-muted,#6B7280);min-height:45px}',
      '.pfdlv--noanim{transition:none}',
      '.pfdlv.on{opacity:1}',
      /* 1011x 甲：骨架 —— :empty（还没内容）时可见，填充后自动失效。
         此前 min-height:45px 撑开位子 + opacity:0 ⇒ 加载期是一块**看不见的空白**（用户截图）。
         两条 11px 浅灰条与真实两行内容同构，高度仍受 min-height 兜住 ⇒ 不产生跳动。 */
      '.pfdlv:empty{opacity:1}',
      '.pfdlv:empty::before{content:"";display:block;height:11px;width:min(300px,74%);border-radius:6px;background:var(--pf-skel,#EDEAF6);animation:pfdlv-sk 1.35s ease-in-out infinite}',
      '.pfdlv:empty::after{content:"";display:block;height:11px;width:min(180px,44%);border-radius:6px;background:var(--pf-skel,#EDEAF6);margin-top:9px;animation:pfdlv-sk 1.35s ease-in-out infinite .2s}',
      '.pfdlv--noanim:empty::before,.pfdlv--noanim:empty::after{animation:none}',
      '@keyframes pfdlv-sk{0%,100%{opacity:1}50%{opacity:.45}}',
      '.pfdlv__row{display:flex;align-items:center;gap:6px;flex-wrap:wrap}',
      '.pfdlv__row+.pfdlv__row{margin-top:4px}',
      '.pfdlv__flag{font-size:1rem;line-height:1}',
      '.pfdlv__strong{color:var(--hd-ink,#1A1230);font-weight:600}',
      /* 1011m 丙：配送块里的国家名可点 ⇒ 就地切换收货国家（虚线下划线 + hover/focus 变色） */
      '.pfdlv__cc{appearance:none;-webkit-appearance:none;border:0;background:none;padding:0;margin:0;font:inherit;font-weight:600;color:var(--hd-ink,#1A1230);text-align:inherit;cursor:pointer;border-bottom:1px dashed currentColor;transition:color .15s ease}',
      '.pfdlv__cc:hover,.pfdlv__cc:focus-visible{color:var(--hd-purple,#7C3AED);outline:none}',
      /* 国家选择浮层 */
      '.pfdlv-pick{position:fixed;left:-9999px;top:0;z-index:2147482500;width:232px;max-width:calc(100vw - 24px);background:#fff;border:1px solid #E5E1F2;border-radius:var(--pf-r-lg,18px);box-shadow:0 18px 48px rgba(20,15,40,.24);padding:8px;opacity:0;visibility:hidden;transform:translateY(-4px);pointer-events:none;transition:opacity .16s ease,transform .16s ease,visibility 0s linear .16s}',
      '.pfdlv-pick.on{opacity:1;visibility:visible;transform:none;pointer-events:auto;transition:opacity .16s ease,transform .16s ease,visibility 0s}',
      '.pfdlv-pick__hd{padding:6px 8px 4px;font-size:.7rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#8A8299}',
      '.pfdlv-pick__search{width:100%;box-sizing:border-box;border:1px solid #E5E1F2;border-radius:10px;padding:8px 10px;font:inherit;font-size:.85rem;margin:2px 0 6px;color:#2A2340}',
      '.pfdlv-pick__list{list-style:none;margin:0;padding:0;max-height:min(46vh,300px);overflow:auto;-webkit-overflow-scrolling:touch}',
      '.pfdlv-pick__item{display:flex;align-items:center;gap:8px;width:100%;border:0;background:none;padding:8px;border-radius:10px;cursor:pointer;font:inherit;font-size:.85rem;color:#2A2340;text-align:left}',
      '.pfdlv-pick__item:hover,.pfdlv-pick__item:focus-visible{background:#F4F1FD;outline:none}',
      '.pfdlv-pick__flag{width:1.3em;text-align:center}',
      '.pfdlv-pick__name{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}',
      '.pfdlv-pick__tick{color:var(--hd-purple,#7C3AED);font-weight:700}',
      '.pfdlv-pick__more{display:block;width:100%;border:0;border-top:1px solid #F1EEF8;margin-top:4px;padding:9px 8px 5px;background:none;font:inherit;font-size:.8rem;font-weight:700;color:var(--hd-purple,#7C3AED);cursor:pointer;text-align:left}',
      '.pfdlv-pick__more:hover{opacity:.8}',
      '.pfdlv-pick__empty{margin:0;padding:10px 8px;font-size:.82rem;color:#8A8299}',
      '.pfdlv-pick--noanim,.pfdlv-pick--noanim.on{transition:none}',

      /* Size guide 按钮（与 Sizes 标签同行） */
      '.pfdlv-sg-btn{border:0;background:none;padding:0;cursor:pointer;font:inherit;font-size:.78rem;font-weight:600;color:var(--hd-purple,#7C3AED);text-decoration:underline;text-underline-offset:2px}',
      '.pfdlv-sg-btn:hover{opacity:.75}',

      /* 弹窗 */
      '.pfdlv-modal{position:fixed;inset:0;z-index:2147482000;display:none;align-items:center;justify-content:center;padding:16px}',
      '.pfdlv-modal.on{display:flex}',
      '.pfdlv-modal__bd{position:absolute;inset:0;background:rgba(10,8,20,.55);backdrop-filter:blur(3px);-webkit-backdrop-filter:blur(3px)}',
      '.pfdlv-modal__card{position:relative;background:#fff;border-radius:var(--pf-r-lg,18px);box-shadow:0 24px 64px rgba(20,15,40,.28);width:min(560px,100%);max-height:min(86vh,760px);display:flex;flex-direction:column;overflow:hidden}',
      '.pfdlv-modal__hd{display:flex;align-items:center;gap:10px;padding:16px 18px 10px}',
      '.pfdlv-modal__title{margin:0;font-size:1.05rem;font-weight:800;color:var(--hd-ink,#1A1230)}',
      '.pfdlv-modal__x{margin-left:auto;border:0;background:none;font-size:1.1rem;line-height:1;cursor:pointer;color:#6B7280;padding:4px 6px}',
      '.pfdlv-tabs{display:flex;gap:6px;padding:0 18px 10px;border-bottom:1px solid #F1EEF8}',
      '.pfdlv-tab{border:1px solid #E5E1F2;background:#fff;color:#4B4462;font:inherit;font-size:.8rem;font-weight:600;padding:7px 12px;border-radius:999px;cursor:pointer}',
      '.pfdlv-tab.sel{background:var(--hd-purple,#7C3AED);border-color:var(--hd-purple,#7C3AED);color:#fff}',
      '.pfdlv-modal__body{overflow:auto;padding:16px 18px 20px;-webkit-overflow-scrolling:touch}',
      '.pfdlv-unit{margin-left:auto;display:flex;gap:4px}',
      '.pfdlv-unit button{border:1px solid #E5E1F2;background:#fff;color:#4B4462;font:inherit;font-size:.74rem;font-weight:600;padding:4px 9px;border-radius:999px;cursor:pointer}',
      '.pfdlv-unit button.sel{background:#1A1230;border-color:#1A1230;color:#fff}',
      '.pfdlv-sg-img{width:100%;max-width:280px;display:block;margin:0 auto 12px;border-radius:10px;background:#FAF9FE}',
      '.pfdlv-sg-desc{font-size:.82rem;line-height:1.6;color:#4B4462}',
      '.pfdlv-sg-desc h6{margin:10px 0 2px;font-size:.82rem;font-weight:700;color:var(--hd-ink,#1A1230)}',
      '.pfdlv-sg-desc p{margin:0 0 6px}',
      '.pfdlv-sg-desc span{font-family:inherit !important;font-size:inherit !important}',
      '.pfdlv-tbl{width:100%;border-collapse:collapse;margin-top:12px;font-size:.8rem}',
      '.pfdlv-tbl th,.pfdlv-tbl td{border:1px solid #EFEBF8;padding:7px 8px;text-align:center;color:#2A2340}',
      '.pfdlv-tbl th{background:#FAF9FE;font-weight:700}',
      '.pfdlv-tbl td:first-child,.pfdlv-tbl th:first-child{text-align:left;font-weight:600}',
      '.pfdlv-sg-note{margin:12px 0 0;font-size:.76rem;color:#8A8299}',
      '.pfdlv-sg-empty{font-size:.85rem;color:#8A8299;padding:10px 0}'
    ].join('');
    (document.head || document.documentElement).appendChild(s);
  }

  /* ================= ⓪ 国家选择浮层（1011m · 丙） =================
     详情页配送块里「国家名」可点 ⇒ 就地切换收货国家。
     🔴 数据源唯一：读顶栏同一个 #hdShipData（= localization.available_countries），
        不另请求、不另存；提交同样走 /localization（优先复用 #hdShipForm，缺失才自建）。
     🔴 无国家数据 ⇒ 保持纯文本不可点（绝不显示点了没反应的控件）。 */
  var PICK_COMMON = ['US', 'GB', 'DE', 'FR', 'CA', 'AU', 'JP'];
  var pickEl = null, pickAll = null, pickAnchor = null, pickCur = '', pickShowAll = false;
  var pickGuardUntil = 0;   /* 2026-10-10 1010m：浮层打开瞬间的滚动噪声保护窗（ms 时间戳） */
  /* esc() 复用本文件既有实现（:215），不另造一份 */

  function pickCountries() {
    if (pickAll) return pickAll;
    pickAll = [];
    try {
      var el = document.getElementById('hdShipData');
      var a = el ? JSON.parse(el.textContent || '[]') : [];
      if (a && a.length) pickAll = a;
    } catch (e) {}
    return pickAll;
  }

  function submitCountry(cc) {
    cc = String(cc || '').toUpperCase();
    if (!/^[A-Z]{2}$/.test(cc)) return;
    var f = document.getElementById('hdShipForm');
    var c = document.getElementById('hdShipCode');
    if (f && c) { c.value = cc; f.submit(); return; }      /* 复用顶栏同一表单 */
    var nf = document.createElement('form');
    nf.method = 'post'; nf.action = '/localization'; nf.acceptCharset = 'UTF-8';
    nf.style.display = 'none';
    var pairs = [['form_type', 'localization'], ['utf8', '\u2713'], ['_method', 'put'],
                 ['return_to', String(location.pathname || '/')], ['country_code', cc]];
    for (var i = 0; i < pairs.length; i++) {
      var ip = document.createElement('input');
      ip.type = 'hidden'; ip.name = pairs[i][0]; ip.value = pairs[i][1];
      nf.appendChild(ip);
    }
    document.body.appendChild(nf); nf.submit();
  }

  function buildPick() {
    var d = document.createElement('div');
    d.className = 'pfdlv-pick'; d.id = 'pfdlv-pick';
    d.setAttribute('role', 'dialog'); d.setAttribute('aria-label', 'Change shipping country');
    d.innerHTML = '<div class="pfdlv-pick__hd">Ship to</div><div id="pfdlvPickBody"></div>';
    if (NOANIM) d.classList.add('pfdlv-pick--noanim');   /* 尊重 ?pfm=off / prefers-reduced-motion */
    d.addEventListener('click', function (e) {
      var p = e.composedPath ? e.composedPath() : [e.target], i, it = null, more = null;
      for (i = 0; i < p.length; i++) {
        var x = p[i];
        if (!x || !x.classList) continue;
        if (x.classList.contains('pfdlv-pick__more')) { more = x; break; }
        if (x.classList.contains('pfdlv-pick__item')) { it = x; break; }
      }
      if (more) { pickShowAll = true; renderPick(); positionPick(pickAnchor); return; }
      if (it) { submitCountry(it.getAttribute('data-cc')); closePick(); }
    });
    return d;
  }

  function pickListHtml(all, cur) {
    var s = '', i;
    for (i = 0; i < all.length; i++) {
      s += '<li><button type="button" class="pfdlv-pick__item" role="option" data-cc="' + esc(all[i].c) +
        '" data-key="' + esc(String(all[i].n + ' ' + all[i].c).toLowerCase()) + '"' +
        (all[i].c === cur ? ' aria-selected="true"' : '') + '>' +
        '<span class="pfdlv-pick__flag">' + flagEmoji(all[i].c) + '</span>' +
        '<span class="pfdlv-pick__name">' + esc(all[i].n) + '</span>' +
        (all[i].c === cur ? '<span class="pfdlv-pick__tick">\u2713</span>' : '') +
        '</button></li>';
    }
    return s;
  }

  function renderPick() {
    if (!pickEl) return;
    var body = pickEl.querySelector('#pfdlvPickBody');
    if (!body) return;
    var all = pickCountries(), use = all, i, m;
    if (!pickShowAll) {
      m = {};
      for (i = 0; i < all.length; i++) m[all[i].c] = all[i];
      use = [];
      for (i = 0; i < PICK_COMMON.length; i++) if (m[PICK_COMMON[i]]) use.push(m[PICK_COMMON[i]]);
    }
    body.innerHTML =
      (pickShowAll ? '<input type="text" class="pfdlv-pick__search" placeholder="Search country" autocomplete="off" spellcheck="false" />' : '') +
      '<ul class="pfdlv-pick__list" role="listbox" aria-label="Countries">' + pickListHtml(use, pickCur) + '</ul>' +
      '<p class="pfdlv-pick__empty" hidden>No match</p>' +
      (pickShowAll ? '' : '<button type="button" class="pfdlv-pick__more">More countries\u2026</button>');
    var si = body.querySelector('.pfdlv-pick__search');
    if (si) {
      var empty = body.querySelector('.pfdlv-pick__empty');
      si.addEventListener('input', function () {
        var q = String(si.value || '').trim().toLowerCase();
        var its = body.querySelectorAll('.pfdlv-pick__item'), n = 0, k;
        for (k = 0; k < its.length; k++) {
          var hit = !q || String(its[k].getAttribute('data-key') || '').indexOf(q) >= 0;
          its[k].parentNode.hidden = !hit;
          if (hit) n++;
        }
        if (empty) empty.hidden = n > 0;
      });
    }
  }

  function positionPick(anchor) {
    if (!pickEl || !anchor) return;
    var r = anchor.getBoundingClientRect();
    var w = pickEl.offsetWidth || 232, h = pickEl.offsetHeight || 260;
    var vw = window.innerWidth || 360, vh = window.innerHeight || 640;
    var left = Math.min(Math.max(8, r.left), Math.max(8, vw - w - 8));
    var top = r.bottom + 8;
    if (top + h > vh - 8) top = Math.max(8, r.top - h - 8);
    pickEl.style.left = Math.round(left) + 'px';
    pickEl.style.top = Math.round(top) + 'px';
  }

  function openPick(anchor, cur) {
    if (!pickCountries().length) return;
    if (!pickEl) { pickEl = buildPick(); document.body.appendChild(pickEl); }
    pickAnchor = anchor;
    pickCur = String(cur || '').toUpperCase();
    pickShowAll = false;
    renderPick();
    pickEl.classList.add('on');
    positionPick(anchor);
    /* 开浮层即进保护窗：点击自带的惯性/平滑滚动余波不应立刻把它关掉 */
    pickGuardUntil = Date.now() + 400;
  }

  function closePick() {
    if (!pickEl) return;
    pickEl.classList.remove('on');
    pickAnchor = null; pickShowAll = false;
  }

  function togglePick(anchor, cur) {
    if (pickEl && pickEl.classList.contains('on') && pickAnchor === anchor) closePick();
    else openPick(anchor, cur);
  }

  function inPath(p, el) {
    for (var i = 0; i < p.length; i++) { if (p[i] === el) return true; }
    return false;
  }

  /* 🔴 点外部 / Esc / 滚动 ⇒ 关闭；一律 composedPath（e.target 可能已脱链） */
  document.addEventListener('click', function (e) {
    if (!pickEl || !pickEl.classList.contains('on')) return;
    var p = e.composedPath ? e.composedPath() : [e.target];
    if (inPath(p, pickEl) || inPath(p, pickAnchor)) return;
    closePick();
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closePick(); });
  window.addEventListener('resize', closePick);
  window.addEventListener('scroll', function (e) {
    if (!pickEl || !pickEl.classList.contains('on')) return;
    var p = e.composedPath ? e.composedPath() : [];
    if (inPath(p, pickEl)) return;            /* 浮层内部滚动不关 */
    if (Date.now() < pickGuardUntil) return;  /* 2026-10-10 1010m：打开瞬间的滚动余波不关 */
    closePick();
  }, true);

  function mountCCClick(e) {
    var p = e.composedPath ? e.composedPath() : [e.target], i;
    for (i = 0; i < p.length; i++) {
      var x = p[i];
      if (x && x.getAttribute && x.getAttribute('data-cc-pick') !== null) {
        e.preventDefault();
        togglePick(x, x.getAttribute('data-cc-pick'));
        return;
      }
    }
  }

  /* 2026-10-09: 加载即注入 CSS。此前只在 delivery() 跑完 / 弹窗打开时才注入，
     而 US-only 商品在非美地区无 selected variant ⇒ delivery() 开头早退 ⇒ 样式永不注入
     ⇒ Size guide 按钮裸奔成原生灰按钮（用户截图即此现象）。提到顶层一次修好全站。 */
  injectCSS();

  /* ================= ① 预计送达 ================= */
  function delivery(opts) {
    opts = opts || {};
    var mount = opts.mount;
    if (!mount) return;
    /* 2026-10-31：业务性可售/不可售回调 —— 前端据此禁用加购（422 拿不到运费 ⇒ 当前地区不可售）。 */
    var onUnavail = opts.onUnavailable;
    var onAvail = opts.onAvailable;
    var cc = String(opts.country || visitorCountry() || '').toUpperCase();
    var vid = parseInt(opts.variantId, 10);
    var bid = parseInt(opts.bridgeId, 10);   // 兼容: catalogVariantId
    if (!vid && bid) vid = bid;
    if (!cc || !vid) { mount.style.display = 'none'; return; }   // 缺料 ⇒ 不留空壳

    injectCSS();
    /* 1011m：国家数据可用才把国家名做成可点（否则保持纯文本，绝不显示点了没反应的控件） */
    var ccPick = pickCountries().length > 0;
    mount.className = 'pfdlv' + (NOANIM ? ' pfdlv--noanim' : '');
    if (ccPick) mount.addEventListener('click', mountCCClick);
    var key = 'pfdlv_rate_' + cc + '_' + vid;

    function paint(rates, instant) {
      mount.style.display = '';   /* 1010a：成功即复活——清掉失败分支留下的行内 none */
      var r = (rates && rates[0]) || null;
      if (!r) { mount.style.display = 'none'; return; }
      var d1 = r.min_delivery_date || r.minDeliveryDate || '';
      var d2 = r.max_delivery_date || r.maxDeliveryDate || '';
      var rng = fmtRange(d1, d2);
      var rate = money(r.rate);
      if (!rng && !rate) { mount.style.display = 'none'; return; }
      var html = '';
      if (rng) {
        var _ccTag = ccPick
          ? '<button type="button" class="pfdlv__cc" data-cc-pick="' + esc(cc) +
            '" aria-haspopup="dialog" aria-label="Change shipping country">' +
            flagEmoji(cc) + ' ' + esc(countryName(cc)) + '</button>'
          : '<span class="pfdlv__strong">' + flagEmoji(cc) + ' ' + countryName(cc) + '</span>';
        html += '<div class="pfdlv__row"><span class="pfdlv__flag">\uD83D\uDE9A</span>' +
          '<span>Estimated delivery to ' + _ccTag + '</span>' +
          '<span class="pfdlv__strong">' + rng + '</span></div>';
      }
      if (rate) {
        html += '<div class="pfdlv__row"><span>Shipping starts at <span class="pfdlv__strong">' + rate + '</span></span></div>';
      }
      mount.innerHTML = html;
      /* 2026-10-20（用户定·甲/丙）：不再 requestAnimationFrame + 淡入（会造成 280ms 的「慢一拍」）。
         `instant` 参数保留但已无视觉差别 —— 统一同步加 on，保证骨架→内容零闪烁。 */
      mount.classList.add('on');
      if (typeof onAvail === 'function') onAvail(r);
    }

    var c = cacheGet(key);
    if (c) { paint(c, true); return; }

    /* 1011i 乙：页头抢跑的同一请求直接复用，避免第二次跨洋往返。
       仅在 key 完全一致时复用；一次性 —— 重试路径回落到真实 fetch。 */
    var _pre = null;
    try {
      var _pf = window.__PF_DLV_PREFETCH;
      if (_pf && _pf.p && _pf.key === key) _pre = _pf.p;
    } catch (e) {}

    /* 1010a：单发失败不再永久隐藏（跨境链路抖动/偶发 rates 空）。
       失败重试 1 次，仍失败才隐藏；成功路径 paint() 会清掉行内 none。
       1011x 丙：区分「业务性拒绝」与「技术性失败」——
       bridge 对上游 4xx（US-only 商品发非美地址 / 变体不存在）返回 422，
       body 为 {"business":true,"error":…} ⇒ 这是**明确结论**，不是抖动 ⇒ 立即收敛、不重试。
       实测 US-only 商品：白等 4.0s → 1.2s（省掉 1.5s 延迟 + 第二次跨洋请求）。
       🔴 抢跑（__PF_DLV_PREFETCH）路径对 422 同样拿到这份 JSON ⇒ 判断一致生效。 */
    function load(tries) {
      var req;
      if (_pre) { req = _pre; _pre = null; }
      else {
        req = fetch(bridgeOf(opts.bridge) + '/shipping/rates', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ catalogVariantId: vid, quantity: opts.quantity || 1, country: cc })
        }).then(function (r) { return r.json(); });
      }
      req.then(function (j) {
        if (!j || !j.success || !j.rates || !j.rates.length) {
          if (j && (j.business === true || j.error)) { mount.style.display = 'none'; if (typeof onUnavail === 'function') onUnavail(j); return; }
          retry(tries); return;
        }
        cacheSet(key, j.rates);
        paint(j.rates);
      }).catch(function () { retry(tries); });
    }
    function retry(tries) {
      if (tries < 1) { setTimeout(function () { load(tries + 1); }, 1000); return; }
      mount.style.display = 'none';
    }
    load(0);
  }

  /* ================= ② 尺码导购 ================= */
  var modalEl = null;
  var sgState = { unit: 'in', data: null, tab: 0, productId: 0, bridge: '' };

  /* 2026-10-09：Printful CDN 响应头带 `Cross-Origin-Resource-Policy: same-origin`
     ⇒ 任何**直连**的 <img> 一律被浏览器拦（实测 `ERR_BLOCKED_BY_RESPONSE.NotSameOrigin`、
     naturalWidth=0 ⇒ 静默裂图）。尺码图必须走 bridge /image-proxy
     （白名单已含 files.cdn.printful.com）。 */
  function sgImg(u) {
    if (!u) return '';
    if (u.indexOf('bridge.highdeep.net/') !== -1) return u;
    return (sgState.bridge || BRIDGE_DEFAULT) + '/image-proxy?url=' + encodeURIComponent(u) + '&w=1000';
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  /* Printful 的说明 HTML 可信，但仍剥掉脚本/事件属性 */
  function sanitize(html) {
    var t = document.createElement('div');
    t.innerHTML = String(html || '');
    t.querySelectorAll('script,style,iframe,object,embed,link,meta').forEach(function (n) { n.remove(); });
    t.querySelectorAll('*').forEach(function (n) {
      Array.prototype.slice.call(n.attributes).forEach(function (a) {
        if (/^on/i.test(a.name) || /^(href|src)$/i.test(a.name) && /^\s*javascript:/i.test(a.value)) n.removeAttribute(a.name);
      });
    });
    return t.innerHTML;
  }

  function ensureModal() {
    if (modalEl) return modalEl;
    injectCSS();
    var m = document.createElement('div');
    m.className = 'pfdlv-modal';
    m.setAttribute('role', 'dialog');
    m.setAttribute('aria-modal', 'true');
    m.setAttribute('aria-label', 'Size guide');
    m.innerHTML =
      '<div class="pfdlv-modal__bd" data-close="1"></div>' +
      '<div class="pfdlv-modal__card">' +
        '<div class="pfdlv-modal__hd">' +
          '<h3 class="pfdlv-modal__title">Size guide</h3>' +
          '<div class="pfdlv-unit" id="pfdlvUnit">' +
            '<button type="button" data-u="in" class="sel">Inches</button>' +
            '<button type="button" data-u="cm">Cm</button>' +
          '</div>' +
          '<button type="button" class="pfdlv-modal__x" data-close="1" aria-label="Close">\u2715</button>' +
        '</div>' +
        '<div class="pfdlv-tabs" id="pfdlvTabs"></div>' +
        '<div class="pfdlv-modal__body" id="pfdlvBody"></div>' +
      '</div>';
    document.body.appendChild(m);
    m.addEventListener('click', function (e) {
      var t = (e.target && e.target.closest) ? e.target.closest('[data-close]') : null;
      if (t && m.contains(t)) closeModal();
    });
    m.addEventListener('click', function (e) {
      var b = (e.target && e.target.closest) ? e.target.closest('[data-u],[data-tab]') : null;
      if (!b || !m.contains(b)) return;
      var u = b.getAttribute('data-u');
      if (u) { sgState.unit = u; paintModal(); return; }
      var ti = b.getAttribute('data-tab');
      if (ti !== null) { sgState.tab = parseInt(ti, 10) || 0; paintModal(); }
    });
    modalEl = m;
    return m;
  }

  function closeModal() { if (modalEl) modalEl.classList.remove('on'); }

  function conv(v) {
    if (sgState.unit !== 'cm') return String(v);
    var m = String(v).match(/^\s*([\d.]+)\s*$/);
    if (!m) return String(v);
    var n = parseFloat(m[1]) * 2.54;
    return (Math.round(n * 10) / 10).toFixed(1);
  }

  /* Printful 一格里有两种取值形态（实测 product 71）：
       · 单值  { size, value }                —— 如 Length / Width
       · 区间  { size, min_value, max_value } —— 如 Measure yourself 的 Chest（胸围给范围）
     只读 value 会让整个 Chest 行变成一串「—」。区间渲染 "31–34"，两端各自换算单位。 */
  function fmtVal(o) {
    if (o == null) return '\u2014';
    if (typeof o !== 'object') return conv(o);
    if (o.value != null && o.value !== '') return conv(o.value);
    var a = o.min_value, b = o.max_value;
    if (a == null && b == null) return '\u2014';
    if (a == null) return conv(b);
    if (b == null) return conv(a);
    return conv(a) + '\u2013' + conv(b);
  }

  function paintModal() {
    var m = ensureModal();
    var tables = (sgState.data && sgState.data.size_tables) || [];
    var tabs = document.getElementById('pfdlvTabs');
    var body = document.getElementById('pfdlvBody');
    if (!tabs || !body) return;

    if (!tables.length) {
      tabs.innerHTML = '';
      body.innerHTML = '<p class="pfdlv-sg-empty">Size chart is not available for this product.</p>';
      return;
    }
    if (sgState.tab >= tables.length) sgState.tab = 0;

    tabs.innerHTML = tables.map(function (t, i) {
      var label = t.type === 'measure_yourself' ? 'Measure yourself'
        : (t.type === 'product_measure' ? 'Product measurements' : (t.type_label || t.type || ('Table ' + (i + 1))));
      return '<button type="button" class="pfdlv-tab' + (i === sgState.tab ? ' sel' : '') + '" data-tab="' + i + '">' + esc(label) + '</button>';
    }).join('');

    var t = tables[sgState.tab] || {};
    var unitLabel = sgState.unit === 'cm' ? 'cm' : 'in';
    var html = '';
    if (t.image_url) html += '<img class="pfdlv-sg-img" src="' + esc(sgImg(t.image_url)) + '" alt="' + esc(t.type || 'Size guide') + '" loading="lazy" decoding="async">';
    if (t.image_description) html += '<div class="pfdlv-sg-desc">' + sanitize(t.image_description) + '</div>';

    var ms = t.measurements || [];
    if (ms.length) {
      var order = [];
      ms.forEach(function (row) { (row.values || []).forEach(function (v) { if (order.indexOf(v.size) < 0) order.push(v.size); }); });
      html += '<table class="pfdlv-tbl"><thead><tr><th>' + unitLabel + '</th>' +
        order.map(function (s) { return '<th>' + esc(s) + '</th>'; }).join('') + '</tr></thead><tbody>';
      ms.forEach(function (row) {
        var bySize = {};
        (row.values || []).forEach(function (v) { bySize[v.size] = v; });
        html += '<tr><td>' + esc(row.type_label || row.type || '') + '</td>' +
          order.map(function (s) { return '<td>' + esc(fmtVal(bySize[s])) + '</td>'; }).join('') + '</tr>';
      });
      html += '</tbody></table>';
    }
    if (!html) html = '<p class="pfdlv-sg-empty">Size chart is not available for this product.</p>';
    html += '<p class="pfdlv-sg-note">Measurements are provided by suppliers. Product measurements may vary by up to 2\u2033 (5 cm).</p>';
    body.innerHTML = html;

    var uEl = document.getElementById('pfdlvUnit');
    if (uEl) {
      Array.prototype.forEach.call(uEl.querySelectorAll('button'), function (b) {
        b.classList.toggle('sel', (b.getAttribute('data-u') || 'in') === sgState.unit);
      });
    }
  }

  function sizeGuide(opts) {
    opts = opts || {};
    var pid = parseInt(opts.productId, 10);
    var vid = parseInt(opts.variantId, 10);
    if (!pid && !vid) return;
    ensureModal();
    sgState.bridge = bridgeOf(opts.bridge);
    sgState.tab = 0;
    var m = ensureModal();
    m.classList.add('on');

    var key = 'pfdlv_sizes_' + (pid || ('v' + vid));
    var c = cacheGet(key);
    if (c) { sgState.data = c; paintModal(); return; }

    sgState.data = null;
    paintModal();
    document.getElementById('pfdlvBody').innerHTML = '<p class="pfdlv-sg-empty">Loading size chart\u2026</p>';

    var url = sgState.bridge + '/pf/sizes?' + (pid ? ('product_id=' + pid) : ('variant_id=' + vid));
    fetch(url).then(function (r) { return r.json(); }).then(function (j) {
      var d = (j && j.success && j.data) || null;
      if (!d) {
        document.getElementById('pfdlvBody').innerHTML = '<p class="pfdlv-sg-empty">Size chart is not available for this product.</p>';
        return;
      }
      cacheSet(key, d);
      sgState.data = d;
      paintModal();
    }).catch(function () {
      var b = document.getElementById('pfdlvBody');
      if (b) b.innerHTML = '<p class="pfdlv-sg-empty">Could not load the size chart. Please try again.</p>';
    });
  }

  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeModal(); });

  /* 2026-10-09 丁批：简洁浮层对外入口。顶栏那颗 219 国大胶囊已删，改地区收敛到
     商品详情页两处 —— 配送块国家名（本文件自己 wire）与 US-only 提示条（外部调用）。
     外部只需 __pfdlvPick.open(anchorEl, curCC)。
     🔴 守卫走 inPath(p, pickAnchor)：触发元素发起的那次 click 天然不算「点外部」，
        不会被同一次冒泡关掉 ⇒ 不需要 hd-ship 时代那种 openFrom 补丁。 */
  window.__pfdlvPick = {
    open: function (anchor, cur) { openPick(anchor, cur); },
    close: closePick,
    toggle: function (anchor, cur) { togglePick(anchor, cur); },
    countries: pickCountries
  };
  window.PFDelivery = { delivery: delivery, sizeGuide: sizeGuide, close: closeModal, country: visitorCountry };
})();
